import { useState, createContext, useContext, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography } from '@kizen/kds/Typography';
import Switch from '__components/Kizen/Switch';
import Input from '__components/Kizen/Input';
import Select from '__components/Kizen/Select/Outlined';
import {
  fontSizeOptions,
  lineHeightOptions,
  borderWeightOptions,
  lineHeightOptionsLookup,
  borderWeightLookup,
} from 'components/PageBuilder/options';
import ColorPickerInput from 'components/Kizen/ColorPickerInput';
import { grayScale } from 'app/colors';

type DashboardStyleSetting = {
  dropShadow: boolean;
  titleAllCaps: boolean;
  titleFontSize: string;
  titleLineHeight: number;
  topLeftBorderRadius: number;
  topRightBorderRadius: number;
  bottomLeftBorderRadius: number;
  bottomRightBorderRadius: number;
  borderWeight: number;
  titleColor: string;
  borderColor: string;
  titleBackgroundColor: string;
};

type DashboardStyleSettingContextType = {
  settings: DashboardStyleSetting;
  updateSettings: (updates: Partial<DashboardStyleSetting>) => void;
};

export const DEFAULT_DASHLET_STYLE_SETTINGS = {
  dropShadow: false,
  titleAllCaps: true,
  titleFontSize: '12',
  titleLineHeight: 1.5,
  topLeftBorderRadius: 4,
  topRightBorderRadius: 4,
  bottomLeftBorderRadius: 4,
  bottomRightBorderRadius: 4,
  borderColor: '#ffffff',
  borderWeight: 0,
} as DashboardStyleSetting;

export const DashboardStyleSettingsContext = createContext<
  DashboardStyleSettingContextType | undefined
>(undefined);

export const DashboardStyleSettingsProvider = ({
  styleSettings,
  children,
}: {
  styleSettings: DashboardStyleSetting;
  children: ReactNode;
}) => {
  const [settings, setSettings] =
    useState<DashboardStyleSetting>(styleSettings);

  const updateSettings = (updates: Partial<DashboardStyleSetting>) => {
    setSettings((prev) => ({ ...prev, ...updates }));
  };

  return (
    <DashboardStyleSettingsContext.Provider
      value={{ settings, updateSettings }}
    >
      {children}
    </DashboardStyleSettingsContext.Provider>
  );
};

export const useDashboardSettings = () => {
  const context = useContext(DashboardStyleSettingsContext);
  return context;
};

export const MAX_BORDER_RADIUS = 20;

export const DashboardStyleSettings = ({
  onChange,
}: {
  onChange: (settings: DashboardStyleSetting) => void;
}) => {
  const { t } = useTranslation();
  const context = useContext(DashboardStyleSettingsContext);
  if (!context) {
    throw new Error(
      'DashboardStyleSettingsContext must be used within a DashboardStyleSettingsProvider'
    );
  }
  const { settings, updateSettings } = context;
  const {
    dropShadow,
    topLeftBorderRadius,
    topRightBorderRadius,
    bottomLeftBorderRadius,
    bottomRightBorderRadius,
    borderColor,
    borderWeight,
    titleAllCaps,
    titleFontSize,
    titleLineHeight,
    titleColor,
    titleBackgroundColor,
  } = settings;

  const handleChange = (updates: Partial<DashboardStyleSetting>) => {
    updateSettings(updates);
    onChange({ ...settings, ...updates });
  };

  const showInvalidBorderRadiusNumber =
    Number(topLeftBorderRadius) > MAX_BORDER_RADIUS ||
    Number(topRightBorderRadius) > MAX_BORDER_RADIUS ||
    Number(bottomLeftBorderRadius) > MAX_BORDER_RADIUS ||
    Number(bottomRightBorderRadius) > MAX_BORDER_RADIUS;

  const showInvalidBorderRadiusInput =
    isNaN(Number(topLeftBorderRadius)) ||
    isNaN(Number(topRightBorderRadius)) ||
    isNaN(Number(bottomLeftBorderRadius)) ||
    isNaN(Number(bottomRightBorderRadius));

  return (
    <div className="mt-[20px]">
      <Typography className="mb-4" size="lg" variant="header" weight="semibold">
        Style Settings
      </Typography>
      <div className="grid grid-cols-[1.5fr_1fr_1fr] grid-rows-3 gap-x-[25px] gap-y-0 items-center justify-start">
        <div className="mt-[20px]">
          <ColorPickerInput
            label={t('Title Background Color')}
            color={titleBackgroundColor ?? 'rbga(0,0,0,0)'}
            onChange={(color: string) =>
              handleChange({ titleBackgroundColor: color })
            }
            onOpen={() => {}}
          />
        </div>
        <div className="mt-[20px]">
          <ColorPickerInput
            label={t('Title Color')}
            color={titleColor ?? grayScale.dark}
            onChange={(color: string) => {
              handleChange({ titleColor: color });
            }}
            onOpen={() => {}}
          />
        </div>
        <div className="mt-[20px]">
          <ColorPickerInput
            label={t('Border Color')}
            color={borderColor ?? 'rbga(0,0,0,0)'}
            onChange={(color: string) => handleChange({ borderColor: color })}
            onOpen={() => {}}
          />
        </div>
        <div className="mt-[20px]">
          <Select
            fullWidth
            options={lineHeightOptions}
            value={
              (lineHeightOptionsLookup as Record<number, string>)[
                titleLineHeight
              ]
            }
            onChange={(option: any) => {
              handleChange({ titleLineHeight: Number(option.value) });
            }}
            label={t('Block Title Line Height')}
          />
        </div>
        <div className="mt-[20px]">
          <Select
            fullWidth
            options={fontSizeOptions}
            value={titleFontSize}
            onChange={(option: any) => {
              handleChange({ titleFontSize: option.value });
            }}
            label={t('Block Title Font Size')}
          />
        </div>
        <Switch
          textPlacement="regular-top"
          checked={titleAllCaps ?? true}
          onChange={(event: any) =>
            handleChange({ titleAllCaps: event.target.checked })
          }
          label={t('All Caps Title')}
        />
        <div className="pt-[35px] relative">
          <Typography className="absolute top-[-15px] left-0">
            {t('Border Radius')}
          </Typography>
          <div className=" absolute top-[-5px] left-[-10px] mt-[15px] grid grid-cols-4 grid-rows-2 gap-x-[5px] gap-y-[10px] text-center items-start">
            <Input
              className="mx-auto"
              width="40px"
              type="NUMBER"
              value={topLeftBorderRadius}
              onChange={(value: string) => {
                handleChange({
                  topLeftBorderRadius: value === '' ? undefined : Number(value),
                });
              }}
              error={topLeftBorderRadius > MAX_BORDER_RADIUS}
            />
            <Input
              className="mx-auto"
              width="40px"
              type="NUMBER"
              value={topRightBorderRadius}
              onChange={(value: any) => {
                handleChange({
                  topRightBorderRadius:
                    value === '' ? undefined : Number(value),
                });
              }}
              error={topRightBorderRadius > MAX_BORDER_RADIUS}
            />
            <Input
              className="mx-auto"
              width="40px"
              type="NUMBER"
              value={bottomLeftBorderRadius}
              onChange={(value: any) => {
                handleChange({
                  bottomLeftBorderRadius:
                    value === '' ? undefined : Number(value),
                });
              }}
              error={bottomLeftBorderRadius > MAX_BORDER_RADIUS}
            />
            <Input
              className="mx-auto"
              width="40px"
              type="NUMBER"
              value={bottomRightBorderRadius}
              onChange={(value: any) => {
                handleChange({
                  bottomRightBorderRadius:
                    value === '' ? undefined : Number(value),
                });
              }}
              error={bottomRightBorderRadius > MAX_BORDER_RADIUS}
            />
            <Typography className="mx-auto">{t('Top-left')}</Typography>
            <Typography className="mx-auto">{t('Top-right')}</Typography>
            <Typography className="mx-auto">{t('Bot-left')}</Typography>
            <Typography className="mx-auto">{t('Bot-right')}</Typography>
          </div>
          {showInvalidBorderRadiusNumber ? (
            <Typography
              size="sm"
              color="red-500"
              className="absolute top-[80px] left-[-5px]"
            >
              {t('Border radius cannot be greater than {{max}}px', {
                max: MAX_BORDER_RADIUS,
              })}
            </Typography>
          ) : null}
          {showInvalidBorderRadiusInput && !showInvalidBorderRadiusNumber ? (
            <Typography
              size="sm"
              color="red-500"
              className="absolute top-[80px] left-[-5px]"
            >
              {t('Border radius must be a valid number')}
            </Typography>
          ) : null}
        </div>
        <Select
          fullWidth
          data-qa="style-settings-border-radius-select"
          options={borderWeightOptions}
          value={(borderWeightLookup as Record<number, string>)[borderWeight]}
          onChange={(option: any) => {
            handleChange({ borderWeight: Number(option.value) });
          }}
          label={t('Border Weight')}
        />
        <Switch
          textPlacement="regular-top"
          checked={dropShadow ?? false}
          onChange={(event: any) =>
            handleChange({ dropShadow: event.target.checked })
          }
          label={t('Drop Shadow')}
        />
      </div>
    </div>
  );
};
